import { NavLink, Navigate, Outlet, useLocation } from 'react-router';
import { LogOut, ShieldCheck } from 'lucide-react';
import { useAuthStore } from '../lib/auth-store.js';
import { navFor } from './nav.js';
import { cn } from '../components/ui/cn.js';

const ENV = import.meta.env.MODE === 'production' ? 'PRODUCTION' : 'SANDBOX';

/**
 * Desktop-first shell (NFR-18): persistent left nav, top bar with role chip
 * and an UNMISTAKABLE environment banner (AC-ADMGL-09). Pre-MFA sessions are
 * routed to /mfa — no admin route accepts that token anyway (AC-ADM-01.3).
 */
export function AdminShell() {
  const { token, role, roleName, permissions, mfaVerified, email, signOut } = useAuthStore();
  const loc = useLocation();
  if (!token || !role) return <Navigate to="/login" replace state={{ from: loc.pathname }} />;
  if (!mfaVerified) return <Navigate to="/mfa" replace />;
  const sections = navFor(permissions);
  return (
    <div className="flex h-screen min-w-console">
      <aside className="flex w-[228px] shrink-0 flex-col bg-navy-900 text-white">
        <div className="flex items-center gap-2 px-5 py-4">
          <ShieldCheck className="h-5 w-5 text-gold-500" />
          <span className="text-base font-semibold tracking-wide">ONOV Admin</span>
        </div>
        <nav className="scrollbar-on-dark flex-1 overflow-y-auto px-2 py-2" aria-label="Primary">
          {/*
           * A session with no permissions renders no links. Say so rather than
           * showing an empty rail: the operator otherwise cannot tell a role
           * with no powers from a console that failed to load.
           */}
          {sections.length === 0 && (
            <p className="px-3 py-2 text-xs leading-relaxed text-white/50">
              No sections available — this session carries no permissions. Sign out and sign in
              again; if that does not help, ask a Super Admin to check your role.
            </p>
          )}
          {sections.map((s) => (
            <div key={s.path} className="mb-1">
              <NavLink
                to={s.path}
                end={s.path === '/' || !!s.children}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-2.5 rounded-md px-3 py-2 text-sm text-white/80 hover:bg-white/10 hover:text-white',
                    isActive && 'bg-white/15 text-white font-medium',
                  )
                }
              >
                <s.icon className="h-4 w-4 shrink-0" /> {s.label}
              </NavLink>
              {s.children && (
                <div className="ml-6 mt-0.5 space-y-0.5 border-l border-white/10 pl-3">
                  {s.children.map((c) => (
                    <NavLink
                      key={c.path}
                      to={c.path}
                      end
                      className={({ isActive }) =>
                        cn(
                          'block rounded px-2 py-1 text-xs text-white/60 hover:text-white',
                          isActive && 'text-white font-medium',
                        )
                      }
                    >
                      {c.label}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>
        <div className="border-t border-white/10 px-4 py-3 text-xs">
          <div className="truncate text-white/80" title={email ?? ''}>
            {email}
          </div>
          <div className="mt-1 flex items-center justify-between">
            <span className="rounded-pill bg-gold-500/20 px-2 py-0.5 font-medium capitalize text-gold-500">
              {roleName ?? role}
            </span>
            <button
              type="button"
              onClick={signOut}
              className="inline-flex items-center gap-1 text-white/60 hover:text-white"
            >
              <LogOut className="h-3.5 w-3.5" /> Sign out
            </button>
          </div>
        </div>
      </aside>
      <div className="flex min-w-0 flex-1 flex-col">
        <div
          className={cn(
            'flex h-7 items-center justify-center text-[11px] font-semibold uppercase tracking-[0.2em] text-white',
            ENV === 'PRODUCTION' ? 'bg-red-600' : 'bg-grey-500',
          )}
          role="status"
        >
          {ENV}
        </div>
        <main className="min-h-0 flex-1 overflow-auto p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
